import { useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import useScrollMotion from '@/hooks/useScrollMotion';
import type { caseStudies } from '@/mocks/work';

type Project = (typeof caseStudies)[number];

const label = 'font-label text-[11px] uppercase tracking-[0.18em]';

export default function ProjectView({ project, next }: { project: Project; next: Project }) {
  const root = useRef<HTMLElement>(null);
  useScrollMotion(root);
  const [g1, g2, g3] = project.gallery;

  return (
    <>
      <Header />
      <main ref={root} className="overflow-x-clip">
        {/* A. Hero */}
        <section aria-label={project.title} className="bg-foreground-950 text-background-50">
          <div className="px-4 md:px-6 pt-28 md:pt-32 pb-10 md:pb-14">
            <Link to="/work" className={`${label} group inline-flex items-center gap-2 text-background-200 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer`}>
              <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:-translate-x-1"><i className="ri-arrow-left-line" /></span>
              Back to work
            </Link>
            <p className={`${label} mt-10 text-primary-300`} data-reveal="up">{project.no} / {project.category}</p>
            <h1 className="mt-4 font-extrabold uppercase tracking-[-0.065em] leading-[0.84] text-[clamp(3rem,11vw,12rem)]" data-reveal="lines">
              <span className="line-mask"><span className="line-inner">{project.title}</span></span>
            </h1>
          </div>
          <div className="px-4 md:px-6">
            <div className="relative h-[62vw] md:h-[50vw] max-h-[860px] min-h-[320px] overflow-hidden rounded-[18px]" data-reveal="clip-center">
              <img src={project.heroSrc} alt={project.heroAlt} title={`${project.title} ${project.category}`} className="absolute inset-0 h-full w-full object-cover object-top" />
            </div>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6 px-4 md:px-6 py-10 md:py-12">
            {[
              ['Category', project.category],
              ['Location', project.location],
              ['Format', project.format],
              ['Our role', project.role],
            ].map(([k, v]) => (
              <div key={k} className="border-t border-background-50/15 pt-4" data-reveal="up">
                <dt className={`${label} text-background-200`}>{k}</dt>
                <dd className="mt-2 text-sm md:text-base text-background-50">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* B. The Idea */}
        <section aria-label="The idea" className="bg-background-50 px-4 md:px-6 py-24 md:py-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <p className={`${label} md:col-span-3 text-secondary-600`}>The idea</p>
            <p className="md:col-span-9 font-bold tracking-[-0.04em] leading-[1.05] text-foreground-950 text-[clamp(1.6rem,3.6vw,3.6rem)]" data-reveal="up">
              {project.idea}
            </p>
          </div>
        </section>

        {/* C. The Experience */}
        <section aria-label="The experience" className="bg-accent-100 px-4 md:px-6 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-end">
            <div className="lg:col-span-5">
              <p className={`${label} text-foreground-700`}>The experience</p>
              <h2 className="mt-5 font-extrabold tracking-[-0.05em] leading-[0.92] text-foreground-950 text-[clamp(2rem,4vw,4rem)]" data-reveal="lines">
                <span className="line-mask"><span className="line-inner">Designed to</span></span>
                <span className="line-mask"><span className="line-inner text-primary-500">be felt.</span></span>
              </h2>
              <p className="mt-6 text-sm md:text-base leading-relaxed text-foreground-800 max-w-[420px]" data-reveal="up">{project.experience}</p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="relative h-[120vw] sm:h-[90vw] lg:h-[46vw] max-h-[760px] min-h-[380px] overflow-hidden rounded-[18px]" data-reveal="clip">
                <img src={project.expSrc} alt={project.expAlt} title={`${project.title} experience`} className="absolute inset-0 h-full w-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* D. Visual Story */}
        <section aria-label="Visual story" className="bg-background-50 px-4 md:px-6 py-20 md:py-28">
          <p className={`${label} text-secondary-600 mb-8 md:mb-10`}>Visual story</p>
          <div className="relative h-[56vw] md:h-[44vw] max-h-[760px] min-h-[260px] overflow-hidden rounded-[18px]" data-scrub="zoom">
            <img src={g1.src} alt={g1.alt} title={`${project.title} visual story`} className="absolute inset-0 h-full w-full object-cover object-top" />
          </div>
          <div className="mt-4 md:mt-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            <div className="md:col-span-7 relative h-[80vw] md:h-[36vw] max-h-[700px] min-h-[340px] overflow-hidden rounded-[18px]" data-reveal="clip-left">
              <img src={g2.src} alt={g2.alt} title={`${project.title} detail`} className="absolute inset-0 h-full w-full object-cover object-top" />
            </div>
            <div className="md:col-span-5 relative h-[80vw] md:h-[36vw] max-h-[700px] min-h-[340px] overflow-hidden rounded-[18px]" data-reveal="clip-right">
              <img src={g3.src} alt={g3.alt} title={`${project.title} moment`} className="absolute inset-0 h-full w-full object-cover object-top" />
            </div>
          </div>
          <p className="mt-10 font-label text-[10px] uppercase tracking-[0.16em] text-secondary-600">Illustrative portfolio concept</p>
        </section>

        {/* E. Next project */}
        <section aria-label="Next project" className="bg-foreground-950 text-background-50">
          <Link to={`/work/${next.slug}`} className="group relative block h-[80svh] min-h-[460px] overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-300">
            <img src={next.heroSrc} alt={next.heroAlt} className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]" />
            <div className="absolute inset-0 bg-foreground-950/55 group-hover:bg-foreground-950/45 transition-colors duration-700" />
            <div className="relative flex h-full flex-col justify-between px-4 md:px-6 py-10 md:py-12">
              <p className={`${label} text-background-200`}>Next project / {next.no}</p>
              <div>
                <p className={`${label} text-background-200`}>{next.category} / {next.location}</p>
                <span className="mt-3 flex flex-wrap items-end gap-x-6 font-extrabold uppercase tracking-[-0.065em] leading-[0.84] text-[clamp(2.8rem,10vw,11rem)] group-hover:text-primary-300 transition-colors">
                  {next.title}
                  <span className="mb-[0.12em] w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xl md:text-2xl transition-transform group-hover:rotate-45"><i className="ri-arrow-right-up-line" /></span>
                </span>
              </div>
            </div>
          </Link>
        </section>

        <section aria-label="Contact" className="bg-accent-100 px-4 md:px-6 py-12 md:py-14">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <p className="text-xl md:text-2xl font-bold tracking-[-0.03em] text-foreground-950 max-w-[520px]">Have an idea worth bringing to life?</p>
            <Link to="/contact" className="inline-flex w-fit items-center gap-2 rounded-full bg-foreground-950 px-7 py-4 text-sm font-semibold text-background-50 hover:bg-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Start a project
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}